import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import type { IClientCacheStorage } from '../../services/clients';
import { ProviderProfileService } from '../../services/ProviderProfileService';
import type { LaunchSecureDto, PatinfoNewRequestDto, PatinfoProfileDto, PatinfoPutRequestDto, PatinfoSearchInclude, PatinfoSearchTermDto } from '../../types/api/patinfo';
import type { AccountPlaDto, AccountPracInfoResponseDto, AccountSraDto, AdminHtmlAnswerDto, MVarDto } from '../../types/api';
import {
  CLIENTS_TAB_CACHE_KEYS,
  EMPTY_CLIENT_SEARCH_VALUES,
  type ManageClientsTabCache,
  type RecentReportsTabCache,
  type OpenAdminsTabCache,
  type ManageFlagsTabCache,
  type ManageMessagesTabCache,
} from '../../services/clients/ClientCacheContracts';
import {
  resolveClientSearchIncludes,
  runClientProfileSearch,
  runRecentReportsSearch,
  runOpenAdminsSearch,
  runManageFlagsSearch,
  runManageMessagesSearch,
  termsToClientSearchValues,
  isOvershotEmptyPage,
} from '../../services/clients/ClientSearchEngine';
import { applyLaunchToProfileList, applyOptimisticProfileListUpdate, removeLaunchFromProfileList } from '../../services/clients/ClientOptimisticUpdater';
import type { ClientTouchpointSchedule } from '../../services/clients/ClientFormBuilder';
import {
  type ClientEnableFlags,
  DEFAULT_CLIENT_ENABLE_FLAGS,
  ClientsSubsectionPlaceholderView,
  ManageClientsView,
  RecentReportsView,
  OpenAdminsView,
  ManageFlagsView,
  ManageMessagesView,
  type ManageClientsSearchValues,
  type ViewReportParams,
} from './clients';
import { getProGroupsLayoutMaxWidth } from '../../config/configManager';
import { resolveAuthContext } from '../../services/auth/authContext';

interface ProGroupsClientsViewProps {
  storage?: any;
  apiService?: any;
  clientCacheStorage?: IClientCacheStorage;
  initialSubsection?: string;
  onSubsectionChange?: (subsection: string) => void;
  onViewReport?: (params: ViewReportParams) => void;
}

interface SearchState extends ManageClientsTabCache {
  isLoading: boolean;
}

const MANAGE_CLIENTS_TAB_KEY = CLIENTS_TAB_CACHE_KEYS.manageClients;
const RECENT_REPORTS_TAB_KEY = CLIENTS_TAB_CACHE_KEYS.recentReports;
const OPEN_ADMINS_TAB_KEY = CLIENTS_TAB_CACHE_KEYS.openAdmins;
const MANAGE_FLAGS_TAB_KEY = CLIENTS_TAB_CACHE_KEYS.manageFlags;
const MANAGE_MESSAGES_TAB_KEY = CLIENTS_TAB_CACHE_KEYS.manageMessages;

const INITIAL_MANAGE_STATE: SearchState = {
  profiles: [],
  isLoading: false,
  error: null,
  blocknum: '0',
  blocksize: '10',
  hasMore: false,
  lastTerms: [],
  hasSearched: false,
  searchValues: EMPTY_CLIENT_SEARCH_VALUES,
  saveSyncError: null,
};

interface RecentReportsState extends RecentReportsTabCache {
  isLoading: boolean;
}

const INITIAL_RECENT_REPORTS_STATE: RecentReportsState = {
  results: [],
  profiles: [],
  isLoading: false,
  error: null,
  blocknum: '0',
  blocksize: '10',
  hasMore: false,
  hasSearched: false,
  saveSyncError: null,
};

interface OpenAdminsState extends OpenAdminsTabCache {
  isLoading: boolean;
}

const INITIAL_OPEN_ADMINS_STATE: OpenAdminsState = {
  profiles: [],
  isLoading: false,
  error: null,
  blocknum: '0',
  blocksize: '10',
  hasMore: false,
  hasSearched: false,
  saveSyncError: null,
};

interface ManageFlagsState extends ManageFlagsTabCache {
  isLoading: boolean;
}

const INITIAL_MANAGE_FLAGS_STATE: ManageFlagsState = {
  profiles: [],
  isLoading: false,
  error: null,
  blocknum: '0',
  blocksize: '10',
  hasMore: false,
  hasSearched: false,
  saveSyncError: null,
};

interface ManageMessagesState extends ManageMessagesTabCache {
  isLoading: boolean;
}

const INITIAL_MANAGE_MESSAGES_STATE: ManageMessagesState = {
  profiles: [],
  isLoading: false,
  error: null,
  blocknum: '0',
  blocksize: '10',
  hasMore: false,
  hasSearched: false,
  saveSyncError: null,
};

type ClientsSubsection =
  | 'Manage Clients'
  | 'Recent Reports'
  | 'Open Admins'
  | 'Manage Flags'
  | 'Manage Messages';

const resolveClientsSubsection = (initialSubsection?: string): ClientsSubsection => {
  switch (initialSubsection) {
    case 'Recent Reports':
    case 'Open Admins':
    case 'Manage Flags':
    case 'Manage Messages':
      return initialSubsection;
    default:
      return 'Manage Clients';
  }
};

const resolveCacheStorage = (storage?: IClientCacheStorage): IClientCacheStorage | null => {
  return storage
    || ((globalThis as any).__proGroupsClientCacheStorage as IClientCacheStorage | undefined)
    || ((globalThis as any).__childNavClientCacheStorage as IClientCacheStorage | undefined)
    || null;
};

const resolveApiService = (apiService?: any): any => {
  return apiService
    || (globalThis as any).__proGroupsApiService
    || (globalThis as any).__childNavApiService;
};

const resolveUserStore = (): any => resolveAuthContext().userStore;

const resolveAccountId = (): string | null => {
  const userStore = resolveUserStore();
  const state = userStore?.getState ? userStore.getState() : null;
  return state?.session?.user?.familyId
    || state?.session?.familyAccount?.familyId
    || null;
};

const isTruthyValue = (value: string | null | undefined): boolean => {
  if (!value) {
    return false;
  }

  const normalized = value.trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'enabled' || normalized === 'active';
};

const getEnablesMap = (dto: AccountPracInfoResponseDto | null): Record<string, boolean> => {
  const enables = dto?.AccountSettings?.Enables ?? [];
  const map: Record<string, boolean> = {};
  enables.forEach((item) => {
    map[item.Name.trim().toLowerCase()] = isTruthyValue(item.Value);
  });
  return map;
};

const HOW_CONTACT_MVAR_NAME = 'howcontact1';
const OFFICE_ONLINE_ACCESS_TYPE = '30';

const getSendAssessmentOptions = (dto: AccountPracInfoResponseDto | null): AdminHtmlAnswerDto[] => {
  const uVars = dto?.AccountSettings?.UVars ?? [];
  const howContactUVar = uVars.find(
    (uVar) => (uVar.MvarName || '').trim().toLowerCase() === HOW_CONTACT_MVAR_NAME
  );
  return howContactUVar?.HtmlUvarAns ?? [];
};

const getOfficeOnlineSraMembers = (dto: AccountPracInfoResponseDto | null): AccountSraDto[] => {
  const sraList = dto?.SraList ?? [];
  return sraList.filter((sra) => (sra.AccessType || '').trim() === OFFICE_ONLINE_ACCESS_TYPE);
};

const getSelectedAssessmentPlaList = (dto: AccountPracInfoResponseDto | null): AccountPlaDto[] => {
  const plaList = dto?.AccountSettings?.PlaList ?? [];
  return plaList.filter((pla) => !!(pla.AssessmentId && pla.AssessmentId.trim()));
};

const PHI_GENDER_MVAR_NAME = 'phigender';

const getClientPhiFields = (dto: AccountPracInfoResponseDto | null): MVarDto[] => {
  return dto?.AccountSettings?.PracPhi ?? [];
};

const getGenderPhiOptions = (dto: AccountPracInfoResponseDto | null): AdminHtmlAnswerDto[] => {
  const uVars = dto?.AccountSettings?.UVars ?? [];
  const genderUVar = uVars.find(
    (uVar) => (uVar.MvarName || '').trim().toLowerCase() === PHI_GENDER_MVAR_NAME
  );
  return genderUVar?.HtmlUvarAns ?? [];
};

// Account-level active touchpoint schedules (managed via AccountSettingsView's "Enable
// schedule management" edit panel) — the source list for the Schedule section's
// "Enable Touchpoint Schedules" column below. Registries only carries RegistryId/Name/Pos;
// Type ('1' = Monitor, '2' = Age-based) lives on the underlying TouchPointScheduleDto, so
// join back to Touchpoints.Schedules (RegistryId === SchedId, see AccountSettingsView.tsx's
// buildSelectedRegistries) to get it — ProfileRowEditor needs Type to know whether to show
// a Start Date input for a given checked schedule.
const getTouchpointSchedules = (dto: AccountPracInfoResponseDto | null): ClientTouchpointSchedule[] => {
  const registries = dto?.AccountSettings?.Registries ?? [];
  const schedules = dto?.Touchpoints?.Schedules ?? [];
  const scheduleBySchedId = new Map(schedules.map((schedule) => [schedule.SchedId.trim().toLowerCase(), schedule]));

  return registries.map((registry) => {
    const matched = scheduleBySchedId.get(registry.RegistryId.trim().toLowerCase());
    return {
      registryId: registry.RegistryId,
      name: registry.Name || matched?.DName || registry.RegistryId,
      type: matched?.Type ?? '',
    };
  });
};

// Deliberately takes ONLY the persisted account_prac_info enablesMap — NOT
// account_settings_draft_${accountId}. That draft is AccountSettingsView's own local
// in-progress-edit bookkeeping (Record<string, string> '0'/'1'/'-1' values, meant to survive
// a page reload mid-edit there), not an authoritative source of committed settings. Merging
// its string values in here used to silently corrupt every flag: a non-empty string like "0"
// is truthy in JS, so `enable_patrefmgmt: "0"` read as "enabled" everywhere a flag was used in
// a plain `if (enableFlags.x)` check — permanently, since the draft is never cleared and
// survives sign-out/sign-in.
const resolveClientEnableFlags = (
  enablesMap: Record<string, boolean>
): ClientEnableFlags => {
  return {
    enable_patrefmgmt: enablesMap.enable_patrefmgmt ?? DEFAULT_CLIENT_ENABLE_FLAGS.enable_patrefmgmt,
    enable_medinfomgmt: enablesMap.enable_medinfomgmt ?? DEFAULT_CLIENT_ENABLE_FLAGS.enable_medinfomgmt,
    enable_patconnmgmt: enablesMap.enable_patconnmgmt ?? DEFAULT_CLIENT_ENABLE_FLAGS.enable_patconnmgmt,
    enable_messagedirect: enablesMap.enable_messagedirect ?? DEFAULT_CLIENT_ENABLE_FLAGS.enable_messagedirect,
    enable_schedulemgmt: enablesMap.enable_schedulemgmt ?? DEFAULT_CLIENT_ENABLE_FLAGS.enable_schedulemgmt,
    enable_phimgmt: enablesMap.enable_phimgmt ?? DEFAULT_CLIENT_ENABLE_FLAGS.enable_phimgmt,
    enable_idmgmt: enablesMap.enable_idmgmt ?? DEFAULT_CLIENT_ENABLE_FLAGS.enable_idmgmt,
    enable_dobmgmt: enablesMap.enable_dobmgmt ?? DEFAULT_CLIENT_ENABLE_FLAGS.enable_dobmgmt,
    enable_manualscheduling: enablesMap.enable_manualscheduling ?? DEFAULT_CLIENT_ENABLE_FLAGS.enable_manualscheduling,
    enable_touchpointscheduling: enablesMap.enable_touchpointscheduling ?? DEFAULT_CLIENT_ENABLE_FLAGS.enable_touchpointscheduling,
  };
};

/**
 * Mobile-native Clients view for ProGroups.
 * Displays client management interface on mobile platforms.
 * 
 * Platform shells provide navigation and storage adapters; this component owns
 * add/manage client flow behavior using shared services.
 */
export const ProGroupsClientsView: React.FC<ProGroupsClientsViewProps> = ({
  storage,
  apiService,
  clientCacheStorage,
  initialSubsection,
  onSubsectionChange,
  onViewReport,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [manageState, setManageState] = useState<SearchState>(INITIAL_MANAGE_STATE);
  const [manageHydrated, setManageHydrated] = useState(false);
  const [recentReportsState, setRecentReportsState] = useState<RecentReportsState>(INITIAL_RECENT_REPORTS_STATE);
  const [recentReportsHydrated, setRecentReportsHydrated] = useState(false);
  const [openAdminsState, setOpenAdminsState] = useState<OpenAdminsState>(INITIAL_OPEN_ADMINS_STATE);
  const [openAdminsHydrated, setOpenAdminsHydrated] = useState(false);
  const [manageFlagsState, setManageFlagsState] = useState<ManageFlagsState>(INITIAL_MANAGE_FLAGS_STATE);
  const [manageFlagsHydrated, setManageFlagsHydrated] = useState(false);
  const [manageMessagesState, setManageMessagesState] = useState<ManageMessagesState>(INITIAL_MANAGE_MESSAGES_STATE);
  const [manageMessagesHydrated, setManageMessagesHydrated] = useState(false);
  const [isAddMode, setIsAddMode] = useState(initialSubsection === 'Add Client');
  const [enableFlags, setEnableFlags] = useState<ClientEnableFlags>(DEFAULT_CLIENT_ENABLE_FLAGS);
  const [showDob, setShowDob] = useState(true);
  const [sendAssessmentOptions, setSendAssessmentOptions] = useState<AdminHtmlAnswerDto[]>([]);
  const [officeOnlineSraMembers, setOfficeOnlineSraMembers] = useState<AccountSraDto[]>([]);
  const [assessmentOptions, setAssessmentOptions] = useState<AccountPlaDto[]>([]);
  const [clientPhiFields, setClientPhiFields] = useState<MVarDto[]>([]);
  const [genderPhiOptions, setGenderPhiOptions] = useState<AdminHtmlAnswerDto[]>([]);
  const [touchpointSchedules, setTouchpointSchedules] = useState<ClientTouchpointSchedule[]>([]);
  const [searchIncludes, setSearchIncludes] = useState<PatinfoSearchInclude[]>(resolveClientSearchIncludes({}));
  const [expandedProfileId, setExpandedProfileId] = useState<string | null>(null);

  const resolvedCacheStorage = resolveCacheStorage(clientCacheStorage);
  const activeSubsection = resolveClientsSubsection(initialSubsection);
  const maxWidth = getProGroupsLayoutMaxWidth();

  const resolvedApiService = resolveApiService(apiService);

  const profileService = useMemo(
    () => (resolvedApiService ? new ProviderProfileService(resolvedApiService) : null),
    [resolvedApiService]
  );

  useEffect(() => {
    if (resolvedCacheStorage) {
      setIsInitialized(true);
    }
  }, [resolvedCacheStorage]);

  useEffect(() => {
    let cancelled = false;

    const loadManageState = async () => {
      if (!resolvedCacheStorage) {
        setManageHydrated(true);
        return;
      }

      const cached = await resolvedCacheStorage.loadTabState(MANAGE_CLIENTS_TAB_KEY) as Partial<ManageClientsTabCache> | null;
      if (cancelled) {
        return;
      }

      if (cached) {
        setManageState((current) => ({
          ...current,
          ...cached,
          isLoading: false,
          error: cached.error ?? null,
          saveSyncError: cached.saveSyncError ?? null,
          blocksize: cached.blocksize ?? cached.searchValues?.blocksize ?? current.blocksize,
          searchValues: {
            ...(cached.searchValues ?? termsToClientSearchValues(cached.lastTerms ?? current.lastTerms)),
            blocksize: cached.blocksize ?? cached.searchValues?.blocksize ?? current.blocksize,
          },
        }));
      }

      setManageHydrated(true);
    };

    void loadManageState();

    return () => {
      cancelled = true;
    };
  }, [resolvedCacheStorage]);

  useEffect(() => {
    if (!manageHydrated || !resolvedCacheStorage) {
      return;
    }

    void resolvedCacheStorage.saveTabState(MANAGE_CLIENTS_TAB_KEY, {
      ...manageState,
      isLoading: false,
    });
  }, [manageHydrated, manageState, resolvedCacheStorage]);

  useEffect(() => {
    let cancelled = false;

    const loadRecentReportsState = async () => {
      if (!resolvedCacheStorage) {
        setRecentReportsHydrated(true);
        return;
      }

      const cached = await resolvedCacheStorage.loadTabState(RECENT_REPORTS_TAB_KEY) as Partial<RecentReportsTabCache> | null;
      if (cancelled) {
        return;
      }

      if (cached) {
        setRecentReportsState((current) => ({
          ...current,
          ...cached,
          isLoading: false,
          error: cached.error ?? null,
          saveSyncError: cached.saveSyncError ?? null,
        }));
      }

      setRecentReportsHydrated(true);
    };

    void loadRecentReportsState();

    return () => {
      cancelled = true;
    };
  }, [resolvedCacheStorage]);

  useEffect(() => {
    if (!recentReportsHydrated || !resolvedCacheStorage) {
      return;
    }

    void resolvedCacheStorage.saveTabState(RECENT_REPORTS_TAB_KEY, {
      ...recentReportsState,
      isLoading: false,
    });
  }, [recentReportsHydrated, recentReportsState, resolvedCacheStorage]);

  useEffect(() => {
    let cancelled = false;

    const loadOpenAdminsState = async () => {
      if (!resolvedCacheStorage) {
        setOpenAdminsHydrated(true);
        return;
      }

      const cached = await resolvedCacheStorage.loadTabState(OPEN_ADMINS_TAB_KEY) as Partial<OpenAdminsTabCache> | null;
      if (cancelled) {
        return;
      }

      if (cached) {
        setOpenAdminsState((current) => ({
          ...current,
          ...cached,
          isLoading: false,
          error: cached.error ?? null,
          saveSyncError: cached.saveSyncError ?? null,
        }));
      }

      setOpenAdminsHydrated(true);
    };

    void loadOpenAdminsState();

    return () => {
      cancelled = true;
    };
  }, [resolvedCacheStorage]);

  useEffect(() => {
    if (!openAdminsHydrated || !resolvedCacheStorage) {
      return;
    }

    void resolvedCacheStorage.saveTabState(OPEN_ADMINS_TAB_KEY, {
      ...openAdminsState,
      isLoading: false,
    });
  }, [openAdminsHydrated, openAdminsState, resolvedCacheStorage]);

  useEffect(() => {
    let cancelled = false;

    const loadManageFlagsState = async () => {
      if (!resolvedCacheStorage) {
        setManageFlagsHydrated(true);
        return;
      }

      const cached = await resolvedCacheStorage.loadTabState(MANAGE_FLAGS_TAB_KEY) as Partial<ManageFlagsTabCache> | null;
      if (cancelled) {
        return;
      }

      if (cached) {
        setManageFlagsState((current) => ({
          ...current,
          ...cached,
          isLoading: false,
          error: cached.error ?? null,
          saveSyncError: cached.saveSyncError ?? null,
        }));
      }

      setManageFlagsHydrated(true);
    };

    void loadManageFlagsState();

    return () => {
      cancelled = true;
    };
  }, [resolvedCacheStorage]);

  useEffect(() => {
    if (!manageFlagsHydrated || !resolvedCacheStorage) {
      return;
    }

    void resolvedCacheStorage.saveTabState(MANAGE_FLAGS_TAB_KEY, {
      ...manageFlagsState,
      isLoading: false,
    });
  }, [manageFlagsHydrated, manageFlagsState, resolvedCacheStorage]);

  useEffect(() => {
    let cancelled = false;

    const loadManageMessagesState = async () => {
      if (!resolvedCacheStorage) {
        setManageMessagesHydrated(true);
        return;
      }

      const cached = await resolvedCacheStorage.loadTabState(MANAGE_MESSAGES_TAB_KEY) as Partial<ManageMessagesTabCache> | null;
      if (cancelled) {
        return;
      }

      if (cached) {
        setManageMessagesState((current) => ({
          ...current,
          ...cached,
          isLoading: false,
          error: cached.error ?? null,
          saveSyncError: cached.saveSyncError ?? null,
        }));
      }

      setManageMessagesHydrated(true);
    };

    void loadManageMessagesState();

    return () => {
      cancelled = true;
    };
  }, [resolvedCacheStorage]);

  useEffect(() => {
    if (!manageMessagesHydrated || !resolvedCacheStorage) {
      return;
    }

    void resolvedCacheStorage.saveTabState(MANAGE_MESSAGES_TAB_KEY, {
      ...manageMessagesState,
      isLoading: false,
    });
  }, [manageMessagesHydrated, manageMessagesState, resolvedCacheStorage]);

  useEffect(() => {
    setIsAddMode(initialSubsection === 'Add Client');
  }, [initialSubsection]);

  // Guards state updates from a load that resolves after this component has unmounted —
  // shared by both the mount-time load below and the event-driven refresh, since either
  // one may still be in flight when the view unmounts.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadEnableFlags = useCallback(async () => {
    if (!storage?.getItem) {
      if (isMountedRef.current) {
        setEnableFlags(DEFAULT_CLIENT_ENABLE_FLAGS);
      }
      return;
    }

    const accountId = resolveAccountId();
    if (!accountId) {
      if (isMountedRef.current) {
        setEnableFlags(DEFAULT_CLIENT_ENABLE_FLAGS);
        setSendAssessmentOptions([]);
        setOfficeOnlineSraMembers([]);
        setAssessmentOptions([]);
        setClientPhiFields([]);
        setGenderPhiOptions([]);
        setTouchpointSchedules([]);
        setSearchIncludes(resolveClientSearchIncludes({}));
      }
      return;
    }

    let enablesMap: Record<string, boolean> = {};
    let contactOptions: AdminHtmlAnswerDto[] = [];
    let sraMembers: AccountSraDto[] = [];
    let selectedAssessments: AccountPlaDto[] = [];
    let phiFields: MVarDto[] = [];
    let genderOptions: AdminHtmlAnswerDto[] = [];
    let registries: ClientTouchpointSchedule[] = [];

    try {
      const accountRaw = await storage.getItem(`account_prac_info_${accountId}`);
      if (accountRaw) {
        const parsed = JSON.parse(accountRaw) as AccountPracInfoResponseDto;
        enablesMap = getEnablesMap(parsed);
        contactOptions = getSendAssessmentOptions(parsed);
        sraMembers = getOfficeOnlineSraMembers(parsed);
        selectedAssessments = getSelectedAssessmentPlaList(parsed);
        phiFields = getClientPhiFields(parsed);
        genderOptions = getGenderPhiOptions(parsed);
        registries = getTouchpointSchedules(parsed);
      }
    } catch (error) {
      console.warn('ProGroupsClientsView: failed to load account enable flags, using defaults', error);
    }

    if (isMountedRef.current) {
      const flags = resolveClientEnableFlags(enablesMap);
      setEnableFlags(flags);
      setShowDob(flags.enable_dobmgmt);
      setSendAssessmentOptions(contactOptions);
      setOfficeOnlineSraMembers(sraMembers);
      setAssessmentOptions(selectedAssessments);
      setClientPhiFields(phiFields);
      setGenderPhiOptions(genderOptions);
      setTouchpointSchedules(registries);
      setSearchIncludes(resolveClientSearchIncludes(enablesMap));
    }
  }, [storage]);

  useEffect(() => {
    void loadEnableFlags();
  }, [loadEnableFlags]);

  // account_prac_info_${accountId} (read above) is populated asynchronously — by the login/
  // bootstrap sequence, by an account switch, or by AccountSettingsView/TouchpointsView saves —
  // any of which can resolve after this component's own mount-time read above already ran
  // against an empty or stale cache. Without this listener, enablesMap silently stays `{}`
  // forever and resolveClientEnableFlags's `??` fallback makes every section (and the
  // Schedule sub-columns) render as if every enable were on, regardless of the account's
  // actual Settings. Re-run the same load on every occurrence of the event this app already
  // dispatches after each of those refreshes (see childNavStores.ts, userStore.ts,
  // AccountSettingsView.tsx, TouchpointsView.tsx, ProGroupsAccountView.tsx).
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
      return;
    }

    const handleConfigUpdated = () => {
      void loadEnableFlags();
    };

    window.addEventListener('progroups:sra-config-updated', handleConfigUpdated);
    return () => {
      window.removeEventListener('progroups:sra-config-updated', handleConfigUpdated);
    };
  }, [loadEnableFlags]);

  const runSearch = async (terms: PatinfoSearchTermDto[], blocknum: string, searchValues?: ManageClientsSearchValues) => {
    if (!profileService) {
      return;
    }

    const resolvedValues = searchValues ?? termsToClientSearchValues(terms);
    const resolvedBlockSize = resolvedValues.blocksize || manageState.blocksize;
    setExpandedProfileId(null);
    setManageState(s => ({
      ...s,
      isLoading: true,
      error: null,
      blocksize: resolvedBlockSize,
      searchValues: { ...resolvedValues, blocksize: resolvedBlockSize },
    }));
    try {
      const result = await runClientProfileSearch(
        profileService,
        terms,
        blocknum,
        { ...resolvedValues, blocksize: resolvedBlockSize },
        resolvedBlockSize,
        searchIncludes
      );
      setManageState((current) => {
        if (isOvershotEmptyPage(blocknum, result.profiles.length)) {
          return { ...current, isLoading: false, error: null, hasSearched: true, saveSyncError: null, hasMore: false };
        }
        return {
          ...result,
          isLoading: false,
          error: null,
          hasSearched: true,
          saveSyncError: null,
          searchValues: { ...result.searchValues, blocksize: result.blocksize || resolvedBlockSize },
        };
      });
    } catch (err: any) {
      setManageState(s => ({
        ...s,
        isLoading: false,
        error: err?.message ?? 'Search failed. Please try again.',
        hasSearched: true,
      }));
    }
  };

  const runRecentSearch = async (blocknum: string) => {
    if (!profileService) {
      return;
    }

    setRecentReportsState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await runRecentReportsSearch(
        profileService,
        blocknum,
        recentReportsState.blocksize,
        searchIncludes
      );
      setRecentReportsState((current) => {
        if (isOvershotEmptyPage(blocknum, result.results.length)) {
          return { ...current, isLoading: false, error: null, hasSearched: true, saveSyncError: null, hasMore: false };
        }
        return {
          ...result,
          isLoading: false,
          error: null,
          hasSearched: true,
          saveSyncError: null,
        };
      });
    } catch (err: any) {
      setRecentReportsState((s) => ({
        ...s,
        isLoading: false,
        error: err?.message ?? 'Failed to load recent reports. Please try again.',
        hasSearched: true,
      }));
    }
  };

  // Recent Reports has no search criteria to submit — load on first arrival at the tab
  // rather than showing an empty prompt state, unless a cached result already exists.
  useEffect(() => {
    if (
      activeSubsection === 'Recent Reports'
      && recentReportsHydrated
      && !recentReportsState.hasSearched
      && !recentReportsState.isLoading
      && profileService
    ) {
      void runRecentSearch('0');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubsection, recentReportsHydrated, recentReportsState.hasSearched, recentReportsState.isLoading, profileService]);

  const handleSaveProfileForRecentReports = async (payload: PatinfoPutRequestDto) => {
    if (!profileService) {
      return;
    }

    setRecentReportsState((current) => ({
      ...current,
      saveSyncError: null,
      profiles: applyOptimisticProfileListUpdate(current.profiles, payload),
    }));

    try {
      await profileService.putProfile(payload);
    } catch (err: any) {
      setRecentReportsState((current) => ({
        ...current,
        saveSyncError: err?.message ?? 'Saved locally but failed to sync with backend. Will retry on next save.',
      }));
    }
  };

  const handleSendNowForRecentReports = async (profile: PatinfoProfileDto, request: LaunchSecureDto) => {
    if (!profileService) {
      throw new Error('Profile service is unavailable');
    }

    const result = await profileService.launchSecure(request);
    setRecentReportsState((current) => ({
      ...current,
      profiles: applyLaunchToProfileList(current.profiles, profile.patcode, profile.patientid, result),
    }));
  };

  const handleCancelLaunchForRecentReports = async (profile: PatinfoProfileDto, request: LaunchSecureDto) => {
    if (!profileService) {
      throw new Error('Profile service is unavailable');
    }

    await profileService.launchSecure(request);
    setRecentReportsState((current) => ({
      ...current,
      profiles: removeLaunchFromProfileList(current.profiles, profile.patcode, profile.patientid, request.SSKey),
    }));
  };

  const runOpenAdmins = async (blocknum: string) => {
    if (!profileService) {
      return;
    }

    setOpenAdminsState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await runOpenAdminsSearch(
        profileService,
        blocknum,
        openAdminsState.blocksize,
        searchIncludes
      );
      setOpenAdminsState((current) => {
        if (isOvershotEmptyPage(blocknum, result.profiles.length)) {
          return { ...current, isLoading: false, error: null, hasSearched: true, saveSyncError: null, hasMore: false };
        }
        return {
          ...result,
          isLoading: false,
          error: null,
          hasSearched: true,
          saveSyncError: null,
        };
      });
    } catch (err: any) {
      setOpenAdminsState((s) => ({
        ...s,
        isLoading: false,
        error: err?.message ?? 'Failed to load open admins. Please try again.',
        hasSearched: true,
      }));
    }
  };

  // Open Admins has no search criteria to submit — load on first arrival at the tab
  // rather than showing an empty prompt state, unless a cached result already exists.
  useEffect(() => {
    if (
      activeSubsection === 'Open Admins'
      && openAdminsHydrated
      && !openAdminsState.hasSearched
      && !openAdminsState.isLoading
      && profileService
    ) {
      void runOpenAdmins('0');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubsection, openAdminsHydrated, openAdminsState.hasSearched, openAdminsState.isLoading, profileService]);

  const handleSaveProfileForOpenAdmins = async (payload: PatinfoPutRequestDto) => {
    if (!profileService) {
      return;
    }

    setOpenAdminsState((current) => ({
      ...current,
      saveSyncError: null,
      profiles: applyOptimisticProfileListUpdate(current.profiles, payload),
    }));

    try {
      await profileService.putProfile(payload);
    } catch (err: any) {
      setOpenAdminsState((current) => ({
        ...current,
        saveSyncError: err?.message ?? 'Saved locally but failed to sync with backend. Will retry on next save.',
      }));
    }
  };

  const handleSendNowForOpenAdmins = async (profile: PatinfoProfileDto, request: LaunchSecureDto) => {
    if (!profileService) {
      throw new Error('Profile service is unavailable');
    }

    const result = await profileService.launchSecure(request);
    setOpenAdminsState((current) => ({
      ...current,
      profiles: applyLaunchToProfileList(current.profiles, profile.patcode, profile.patientid, result),
    }));
  };

  const handleCancelLaunchForOpenAdmins = async (profile: PatinfoProfileDto, request: LaunchSecureDto) => {
    if (!profileService) {
      throw new Error('Profile service is unavailable');
    }

    await profileService.launchSecure(request);
    setOpenAdminsState((current) => ({
      ...current,
      profiles: removeLaunchFromProfileList(current.profiles, profile.patcode, profile.patientid, request.SSKey),
    }));
  };

  const runManageFlags = async (blocknum: string) => {
    if (!profileService) {
      return;
    }

    setManageFlagsState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await runManageFlagsSearch(
        profileService,
        blocknum,
        manageFlagsState.blocksize,
        searchIncludes
      );
      setManageFlagsState((current) => {
        if (isOvershotEmptyPage(blocknum, result.profiles.length)) {
          return { ...current, isLoading: false, error: null, hasSearched: true, saveSyncError: null, hasMore: false };
        }
        return {
          ...result,
          isLoading: false,
          error: null,
          hasSearched: true,
          saveSyncError: null,
        };
      });
    } catch (err: any) {
      setManageFlagsState((s) => ({
        ...s,
        isLoading: false,
        error: err?.message ?? 'Failed to load flagged clients. Please try again.',
        hasSearched: true,
      }));
    }
  };

  // Manage Flags has no search criteria to submit — load on first arrival at the tab
  // rather than showing an empty prompt state, unless a cached result already exists.
  useEffect(() => {
    if (
      activeSubsection === 'Manage Flags'
      && manageFlagsHydrated
      && !manageFlagsState.hasSearched
      && !manageFlagsState.isLoading
      && profileService
    ) {
      void runManageFlags('0');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubsection, manageFlagsHydrated, manageFlagsState.hasSearched, manageFlagsState.isLoading, profileService]);

  const handleSaveProfileForManageFlags = async (payload: PatinfoPutRequestDto) => {
    if (!profileService) {
      return;
    }

    setManageFlagsState((current) => ({
      ...current,
      saveSyncError: null,
      profiles: applyOptimisticProfileListUpdate(current.profiles, payload),
    }));

    try {
      await profileService.putProfile(payload);
    } catch (err: any) {
      setManageFlagsState((current) => ({
        ...current,
        saveSyncError: err?.message ?? 'Saved locally but failed to sync with backend. Will retry on next save.',
      }));
    }
  };

  const handleSendNowForManageFlags = async (profile: PatinfoProfileDto, request: LaunchSecureDto) => {
    if (!profileService) {
      throw new Error('Profile service is unavailable');
    }

    const result = await profileService.launchSecure(request);
    setManageFlagsState((current) => ({
      ...current,
      profiles: applyLaunchToProfileList(current.profiles, profile.patcode, profile.patientid, result),
    }));
  };

  const handleCancelLaunchForManageFlags = async (profile: PatinfoProfileDto, request: LaunchSecureDto) => {
    if (!profileService) {
      throw new Error('Profile service is unavailable');
    }

    await profileService.launchSecure(request);
    setManageFlagsState((current) => ({
      ...current,
      profiles: removeLaunchFromProfileList(current.profiles, profile.patcode, profile.patientid, request.SSKey),
    }));
  };

  const runManageMessages = async (blocknum: string) => {
    if (!profileService) {
      return;
    }

    setManageMessagesState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const result = await runManageMessagesSearch(
        profileService,
        blocknum,
        manageMessagesState.blocksize,
        searchIncludes
      );
      setManageMessagesState((current) => {
        if (isOvershotEmptyPage(blocknum, result.profiles.length)) {
          return { ...current, isLoading: false, error: null, hasSearched: true, saveSyncError: null, hasMore: false };
        }
        return {
          ...result,
          isLoading: false,
          error: null,
          hasSearched: true,
          saveSyncError: null,
        };
      });
    } catch (err: any) {
      setManageMessagesState((s) => ({
        ...s,
        isLoading: false,
        error: err?.message ?? 'Failed to load clients with pending messages. Please try again.',
        hasSearched: true,
      }));
    }
  };

  // Manage Messages has no search criteria to submit — load on first arrival at the tab
  // rather than showing an empty prompt state, unless a cached result already exists.
  useEffect(() => {
    if (
      activeSubsection === 'Manage Messages'
      && manageMessagesHydrated
      && !manageMessagesState.hasSearched
      && !manageMessagesState.isLoading
      && profileService
    ) {
      void runManageMessages('0');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubsection, manageMessagesHydrated, manageMessagesState.hasSearched, manageMessagesState.isLoading, profileService]);

  const handleSaveProfileForManageMessages = async (payload: PatinfoPutRequestDto) => {
    if (!profileService) {
      return;
    }

    setManageMessagesState((current) => ({
      ...current,
      saveSyncError: null,
      profiles: applyOptimisticProfileListUpdate(current.profiles, payload),
    }));

    try {
      await profileService.putProfile(payload);
    } catch (err: any) {
      setManageMessagesState((current) => ({
        ...current,
        saveSyncError: err?.message ?? 'Saved locally but failed to sync with backend. Will retry on next save.',
      }));
    }
  };

  const handleSendNowForManageMessages = async (profile: PatinfoProfileDto, request: LaunchSecureDto) => {
    if (!profileService) {
      throw new Error('Profile service is unavailable');
    }

    const result = await profileService.launchSecure(request);
    setManageMessagesState((current) => ({
      ...current,
      profiles: applyLaunchToProfileList(current.profiles, profile.patcode, profile.patientid, result),
    }));
  };

  const handleCancelLaunchForManageMessages = async (profile: PatinfoProfileDto, request: LaunchSecureDto) => {
    if (!profileService) {
      throw new Error('Profile service is unavailable');
    }

    await profileService.launchSecure(request);
    setManageMessagesState((current) => ({
      ...current,
      profiles: removeLaunchFromProfileList(current.profiles, profile.patcode, profile.patientid, request.SSKey),
    }));
  };

  const handleSaveProfile = async (payload: PatinfoPutRequestDto) => {
    if (!profileService) {
      return;
    }

    // Apply the optimistic update immediately for a snappy row UI, but do NOT
    // resolve until the real backend write completes. Previously the network
    // call ran in a detached `void (async () => {...})()` that this function
    // never awaited, so "Update" reported success (and the button re-enabled)
    // while the PUT was still in flight — a user who immediately clicked
    // "Client Search" could have that search's read race ahead of the write,
    // reloading pre-save data and making the just-saved edit look "wiped".
    setManageState((current) => ({
      ...current,
      saveSyncError: null,
      profiles: applyOptimisticProfileListUpdate(current.profiles, payload),
    }));

    try {
      await profileService.putProfile(payload);
    } catch (err: any) {
      setManageState((current) => ({
        ...current,
        saveSyncError: err?.message ?? 'Saved locally but failed to sync with backend. Will retry on next save.',
      }));
    }
  };

  const handleSendNow = async (profile: PatinfoProfileDto, request: LaunchSecureDto) => {
    if (!profileService) {
      throw new Error('Profile service is unavailable');
    }

    const result = await profileService.launchSecure(request);
    setManageState((current) => ({
      ...current,
      profiles: applyLaunchToProfileList(current.profiles, profile.patcode, profile.patientid, result),
    }));
  };

  const handleCancelLaunch = async (profile: PatinfoProfileDto, request: LaunchSecureDto) => {
    if (!profileService) {
      throw new Error('Profile service is unavailable');
    }

    await profileService.launchSecure(request);
    setManageState((current) => ({
      ...current,
      profiles: removeLaunchFromProfileList(current.profiles, profile.patcode, profile.patientid, request.SSKey),
    }));
  };

  const handleAddSave = async (payload: PatinfoNewRequestDto) => {
    if (!profileService) {
      throw new Error('Profile service is unavailable');
    }
    const result = await profileService.newProfile(payload);
    if (result.profile) {
      const newProfile = result.profile;
      const newId = newProfile.patcode || newProfile.patientid || null;
      setManageState(current => ({
        ...current,
        profiles: [newProfile, ...current.profiles],
        hasSearched: true,
      }));
      setExpandedProfileId(newId);
    }
  };

  const handleCancelAdd = () => {
    setIsAddMode(false);
    onSubsectionChange?.('Manage Clients');
  };

  if (!isInitialized) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading Clients...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={[styles.mainCardFrame, { maxWidth }]}> 
        {activeSubsection === 'Manage Clients' ? (
          <ManageClientsView
            values={manageState.searchValues}
            onValuesChange={(values) => {
              setManageState((current) => ({
                ...current,
                blocksize: values.blocksize || current.blocksize,
                searchValues: {
                  ...values,
                  blocksize: values.blocksize || current.blocksize,
                },
              }));
            }}
            onSearch={(terms, values) => {
              const resolvedValues = {
                ...values,
                blocksize: values.blocksize || manageState.blocksize,
              };
              void runSearch(terms, '0', resolvedValues);
            }}
            profiles={manageState.profiles}
            isLoading={manageState.isLoading}
            error={manageState.error}
            hasSearched={manageState.hasSearched}
            blocknum={manageState.blocknum}
            blocksize={manageState.blocksize}
            hasMore={manageState.hasMore}
            saveSyncError={manageState.saveSyncError}
            enableFlags={enableFlags}
            onNextPage={() => {
              const next = String(parseInt(manageState.blocknum, 10) + 1);
              void runSearch(manageState.lastTerms, next, manageState.searchValues);
            }}
            onPrevPage={() => {
              const prev = String(Math.max(0, parseInt(manageState.blocknum, 10) - 1));
              void runSearch(manageState.lastTerms, prev, manageState.searchValues);
            }}
            onSaveProfile={handleSaveProfile}
            onSendNow={handleSendNow}
            onCancelLaunch={handleCancelLaunch}
            onViewReport={onViewReport}
            isAddMode={isAddMode}
            onAdd={handleAddSave}
            onCancelAdd={handleCancelAdd}
            showDob={showDob}
            expandedProfileId={expandedProfileId}
            sendAssessmentOptions={sendAssessmentOptions}
            officeOnlineSraMembers={officeOnlineSraMembers}
            assessmentOptions={assessmentOptions}
            clientPhiFields={clientPhiFields}
            genderPhiOptions={genderPhiOptions}
            touchpointSchedules={touchpointSchedules}
          />
        ) : activeSubsection === 'Recent Reports' ? (
          <RecentReportsView
            results={recentReportsState.results}
            profiles={recentReportsState.profiles}
            isLoading={recentReportsState.isLoading}
            error={recentReportsState.error}
            blocknum={recentReportsState.blocknum}
            blocksize={recentReportsState.blocksize}
            hasMore={recentReportsState.hasMore}
            saveSyncError={recentReportsState.saveSyncError}
            enableFlags={enableFlags}
            searchValues={manageState.searchValues}
            onSearchValuesChange={(values) => {
              setManageState((current) => ({
                ...current,
                blocksize: values.blocksize || current.blocksize,
                searchValues: {
                  ...values,
                  blocksize: values.blocksize || current.blocksize,
                },
              }));
            }}
            onSearchSubmit={(terms, values) => {
              // Submitting the shared search bar from Recent Reports switches to Manage
              // Clients and runs the normal type:'profiles' search there — Recent Reports'
              // own state/results are untouched, so tabbing back shows what was there before.
              onSubsectionChange?.('Manage Clients');
              const resolvedValues = {
                ...values,
                blocksize: values.blocksize || manageState.blocksize,
              };
              void runSearch(terms, '0', resolvedValues);
            }}
            onNextPage={() => {
              const next = String(parseInt(recentReportsState.blocknum, 10) + 1);
              void runRecentSearch(next);
            }}
            onPrevPage={() => {
              const prev = String(Math.max(0, parseInt(recentReportsState.blocknum, 10) - 1));
              void runRecentSearch(prev);
            }}
            onRefresh={() => void runRecentSearch('0')}
            onSaveProfile={handleSaveProfileForRecentReports}
            onSendNow={handleSendNowForRecentReports}
            onCancelLaunch={handleCancelLaunchForRecentReports}
            onViewReport={onViewReport}
            showDob={showDob}
            sendAssessmentOptions={sendAssessmentOptions}
            officeOnlineSraMembers={officeOnlineSraMembers}
            assessmentOptions={assessmentOptions}
            clientPhiFields={clientPhiFields}
            genderPhiOptions={genderPhiOptions}
            touchpointSchedules={touchpointSchedules}
          />
        ) : activeSubsection === 'Open Admins' ? (
          <OpenAdminsView
            profiles={openAdminsState.profiles}
            isLoading={openAdminsState.isLoading}
            error={openAdminsState.error}
            blocknum={openAdminsState.blocknum}
            blocksize={openAdminsState.blocksize}
            hasMore={openAdminsState.hasMore}
            saveSyncError={openAdminsState.saveSyncError}
            enableFlags={enableFlags}
            searchValues={manageState.searchValues}
            onSearchValuesChange={(values) => {
              setManageState((current) => ({
                ...current,
                blocksize: values.blocksize || current.blocksize,
                searchValues: {
                  ...values,
                  blocksize: values.blocksize || current.blocksize,
                },
              }));
            }}
            onSearchSubmit={(terms, values) => {
              // Submitting the shared search bar from Open Admins switches to Manage
              // Clients and runs the normal type:'profiles' search there — Open Admins'
              // own state/results are untouched, so tabbing back shows what was there before.
              onSubsectionChange?.('Manage Clients');
              const resolvedValues = {
                ...values,
                blocksize: values.blocksize || manageState.blocksize,
              };
              void runSearch(terms, '0', resolvedValues);
            }}
            onNextPage={() => {
              const next = String(parseInt(openAdminsState.blocknum, 10) + 1);
              void runOpenAdmins(next);
            }}
            onPrevPage={() => {
              const prev = String(Math.max(0, parseInt(openAdminsState.blocknum, 10) - 1));
              void runOpenAdmins(prev);
            }}
            onRefresh={() => void runOpenAdmins('0')}
            onSaveProfile={handleSaveProfileForOpenAdmins}
            onSendNow={handleSendNowForOpenAdmins}
            onCancelLaunch={handleCancelLaunchForOpenAdmins}
            onViewReport={onViewReport}
            showDob={showDob}
            sendAssessmentOptions={sendAssessmentOptions}
            officeOnlineSraMembers={officeOnlineSraMembers}
            assessmentOptions={assessmentOptions}
            clientPhiFields={clientPhiFields}
            genderPhiOptions={genderPhiOptions}
            touchpointSchedules={touchpointSchedules}
          />
        ) : activeSubsection === 'Manage Flags' ? (
          <ManageFlagsView
            profiles={manageFlagsState.profiles}
            isLoading={manageFlagsState.isLoading}
            error={manageFlagsState.error}
            blocknum={manageFlagsState.blocknum}
            blocksize={manageFlagsState.blocksize}
            hasMore={manageFlagsState.hasMore}
            saveSyncError={manageFlagsState.saveSyncError}
            enableFlags={enableFlags}
            searchValues={manageState.searchValues}
            onSearchValuesChange={(values) => {
              setManageState((current) => ({
                ...current,
                blocksize: values.blocksize || current.blocksize,
                searchValues: {
                  ...values,
                  blocksize: values.blocksize || current.blocksize,
                },
              }));
            }}
            onSearchSubmit={(terms, values) => {
              // Submitting the shared search bar from Manage Flags switches to Manage
              // Clients and runs the normal type:'profiles' search there — Manage Flags'
              // own state/results are untouched, so tabbing back shows what was there before.
              onSubsectionChange?.('Manage Clients');
              const resolvedValues = {
                ...values,
                blocksize: values.blocksize || manageState.blocksize,
              };
              void runSearch(terms, '0', resolvedValues);
            }}
            onNextPage={() => {
              const next = String(parseInt(manageFlagsState.blocknum, 10) + 1);
              void runManageFlags(next);
            }}
            onPrevPage={() => {
              const prev = String(Math.max(0, parseInt(manageFlagsState.blocknum, 10) - 1));
              void runManageFlags(prev);
            }}
            onRefresh={() => void runManageFlags('0')}
            onSaveProfile={handleSaveProfileForManageFlags}
            onSendNow={handleSendNowForManageFlags}
            onCancelLaunch={handleCancelLaunchForManageFlags}
            onViewReport={onViewReport}
            showDob={showDob}
            sendAssessmentOptions={sendAssessmentOptions}
            officeOnlineSraMembers={officeOnlineSraMembers}
            assessmentOptions={assessmentOptions}
            clientPhiFields={clientPhiFields}
            genderPhiOptions={genderPhiOptions}
            touchpointSchedules={touchpointSchedules}
          />
        ) : activeSubsection === 'Manage Messages' ? (
          <ManageMessagesView
            profiles={manageMessagesState.profiles}
            isLoading={manageMessagesState.isLoading}
            error={manageMessagesState.error}
            blocknum={manageMessagesState.blocknum}
            blocksize={manageMessagesState.blocksize}
            hasMore={manageMessagesState.hasMore}
            saveSyncError={manageMessagesState.saveSyncError}
            enableFlags={enableFlags}
            searchValues={manageState.searchValues}
            onSearchValuesChange={(values) => {
              setManageState((current) => ({
                ...current,
                blocksize: values.blocksize || current.blocksize,
                searchValues: {
                  ...values,
                  blocksize: values.blocksize || current.blocksize,
                },
              }));
            }}
            onSearchSubmit={(terms, values) => {
              // Submitting the shared search bar from Manage Messages switches to Manage
              // Clients and runs the normal type:'profiles' search there — Manage Messages'
              // own state/results are untouched, so tabbing back shows what was there before.
              onSubsectionChange?.('Manage Clients');
              const resolvedValues = {
                ...values,
                blocksize: values.blocksize || manageState.blocksize,
              };
              void runSearch(terms, '0', resolvedValues);
            }}
            onNextPage={() => {
              const next = String(parseInt(manageMessagesState.blocknum, 10) + 1);
              void runManageMessages(next);
            }}
            onPrevPage={() => {
              const prev = String(Math.max(0, parseInt(manageMessagesState.blocknum, 10) - 1));
              void runManageMessages(prev);
            }}
            onRefresh={() => void runManageMessages('0')}
            onSaveProfile={handleSaveProfileForManageMessages}
            onSendNow={handleSendNowForManageMessages}
            onCancelLaunch={handleCancelLaunchForManageMessages}
            onViewReport={onViewReport}
            showDob={showDob}
            sendAssessmentOptions={sendAssessmentOptions}
            officeOnlineSraMembers={officeOnlineSraMembers}
            assessmentOptions={assessmentOptions}
            clientPhiFields={clientPhiFields}
            genderPhiOptions={genderPhiOptions}
            touchpointSchedules={touchpointSchedules}
          />
        ) : (
          <ClientsSubsectionPlaceholderView subsectionTitle={activeSubsection} />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#64748b',
  },
  mainCardFrame: {
    width: '100%',
    alignSelf: 'center',
    backgroundColor: '#ffffff',
  },
});

